import React from "react";
import { Heading, Subheading } from "../../design/heading";
import { ChevronLeftIcon, MapPinIcon } from "@heroicons/react/16/solid";
import { Link } from "../../design/link";
import ParentDetailFileList from "./components/Files/ParentDetailFilesList";
import ParentNotesCard from "./components/Notes/ParentNotesCard";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import EditParentButton from "./components/EditParentButton";
import LoadingView from "../../components/LoadingView";
import moment from "moment";
import CustodyStatusBadge from "../../components/CustodyStatusBadge";
import { Avatar } from "../../design/avatar";
import RelationshipToChildBadge from "../../components/RelationshipStatusBadge";
import { Button } from "../../design/button";
import ParentDetailContactList from "./components/Contacts/ParentDetailContactsList";
import { Text } from "../../design/text";
import UpdateContactInfoButton from "./UpdateContactInfoButton";
import { getInitials } from "../../utils/string";
import ErrorView from "../../components/ErrorView";
import {
  ParentHeaderProps,
  ParentBasicInfoProps,
  ParentHealthInfoProps,
  HealthField,
} from "./types";
import { useParentDetail } from "./hooks/useParentDetail";
import { Divider } from "../../design/divider";
import { Badge } from "../../design/badge";
import { Address, Parent, getAttorneyDisplayName } from "@visitproof/types";
import { useAttorney } from "../../contexts/AttorneyContext";
import {
  RACE_LABELS,
  SEX_LABELS,
  LANGUAGE_LABELS,
  MARITAL_STATUS_LABELS,
  INCOME_LEVEL_LABELS,
  EDUCATION_LEVEL_LABELS,
  VICTIMIZATION_TYPE_LABELS,
  Race,
  Sex,
  PrimaryLanguage,
  MaritalStatus,
  IncomeLevel,
  EducationLevel,
  VictimizationType,
} from "@visitproof/types";

// Breadcrumb Navigation
const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/parents"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Parents
    </Link>
  </div>
);

// Parent Header with Avatar and Actions
const ParentHeader: React.FC<ParentHeaderProps> = React.memo(({ parent, isChatEnabled }) => (
  <div className="mt-4 lg:mt-8">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        <Avatar
          src={parent.avatarUrl}
          alt={`${parent.firstName} ${parent.lastName}`}
          className="rounded-full w-14 h-14"
          initials={getInitials(`${parent.firstName} ${parent.lastName}`)}
        />
        <div>
          <Heading>
            {parent.firstName} {parent.lastName}
          </Heading>
          <div className="flex items-center gap-2 mt-1">
            <CustodyStatusBadge status={parent.custodyStatus} />
            {parent.relationshipToChild && (
              <RelationshipToChildBadge status={parent.relationshipToChild} />
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          color="blue"
          href={`/messages?parentId=${parent.id}`}
        >
          Message
        </Button>
        <EditParentButton parent={parent} />
      </div>
    </div>
  </div>
));

// Contact Information Card
interface ContactInfoCardProps {
  parent: ParentBasicInfoProps['parent'];
  caseInfo?: { identifier?: string } | null;
}

const ContactInfoCard: React.FC<ContactInfoCardProps> = React.memo(({ parent, caseInfo }) => (
  <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <Subheading>Contact Information</Subheading>
    <Divider className="my-4" />
    <DescriptionList>
      <DescriptionTerm>Phone Number</DescriptionTerm>
      <DescriptionDetails>
        <div className="flex items-center gap-2">
          {parent.phoneNumber || "Not provided"}
          <UpdateContactInfoButton parent={parent} />
        </div>
      </DescriptionDetails>

      <DescriptionTerm>Email</DescriptionTerm>
      <DescriptionDetails>
        <div className="flex items-center gap-2">
          {parent.email || "Not provided"}
          <UpdateContactInfoButton parent={parent} />
        </div>
      </DescriptionDetails>

      <DescriptionTerm>DOB</DescriptionTerm>
      <DescriptionDetails>
        {parent.dob ? moment(parent.dob.toDate()).format("MMMM Do, YYYY") : "Not provided"}
      </DescriptionDetails>

      <DescriptionTerm>Case</DescriptionTerm>
      <DescriptionDetails>
        <Link
          href={`/case/${parent.caseId}`}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          {caseInfo?.identifier ?? parent.caseId}
        </Link>
      </DescriptionDetails>
    </DescriptionList>
  </div>
));

// Demographics Card
const DemographicsCard: React.FC<{ parent: Parent }> = React.memo(({ parent }) => {
  const hasDemographics = parent.sex || parent.race || parent.primaryLanguage ||
    parent.maritalStatus || parent.incomeLevel || parent.educationLevel ||
    parent.isRuralArea || parent.translatorNeeded;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Demographics</Subheading>
      <Divider className="my-4" />
      {!hasDemographics ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No demographics information recorded. Edit to add demographics for grant reporting.
        </p>
      ) : (
        <DescriptionList>
          {parent.sex && (
            <>
              <DescriptionTerm>Sex</DescriptionTerm>
              <DescriptionDetails>
                {SEX_LABELS[parent.sex as Sex] || parent.sex}
              </DescriptionDetails>
            </>
          )}

          {parent.race && (
            <>
              <DescriptionTerm>Race/Ethnicity</DescriptionTerm>
              <DescriptionDetails>
                {RACE_LABELS[parent.race as Race] || parent.race}
              </DescriptionDetails>
            </>
          )}

          {parent.primaryLanguage && (
            <>
              <DescriptionTerm>Primary Language</DescriptionTerm>
              <DescriptionDetails>
                {parent.primaryLanguage === "other" && parent.primaryLanguageOther
                  ? parent.primaryLanguageOther
                  : LANGUAGE_LABELS[parent.primaryLanguage as PrimaryLanguage] || parent.primaryLanguage}
              </DescriptionDetails>
            </>
          )}

          {parent.translatorNeeded !== undefined && (
            <>
              <DescriptionTerm>Translator Needed</DescriptionTerm>
              <DescriptionDetails>
                <Badge color={parent.translatorNeeded ? "amber" : "zinc"}>
                  {parent.translatorNeeded ? "Yes" : "No"}
                </Badge>
              </DescriptionDetails>
            </>
          )}

          {parent.maritalStatus && (
            <>
              <DescriptionTerm>Marital Status</DescriptionTerm>
              <DescriptionDetails>
                {MARITAL_STATUS_LABELS[parent.maritalStatus as MaritalStatus] || parent.maritalStatus}
              </DescriptionDetails>
            </>
          )}

          {parent.incomeLevel && (
            <>
              <DescriptionTerm>Income Level</DescriptionTerm>
              <DescriptionDetails>
                {INCOME_LEVEL_LABELS[parent.incomeLevel as IncomeLevel] || parent.incomeLevel}
              </DescriptionDetails>
            </>
          )}

          {parent.educationLevel && (
            <>
              <DescriptionTerm>Education Level</DescriptionTerm>
              <DescriptionDetails>
                {EDUCATION_LEVEL_LABELS[parent.educationLevel as EducationLevel] || parent.educationLevel}
              </DescriptionDetails>
            </>
          )}

          {parent.isRuralArea !== undefined && (
            <>
              <DescriptionTerm>Rural Area</DescriptionTerm>
              <DescriptionDetails>
                <Badge color={parent.isRuralArea ? "blue" : "zinc"}>
                  {parent.isRuralArea ? "Yes" : "No"}
                </Badge>
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Address Card
const AddressCard: React.FC<{ address?: Address }> = React.memo(({ address }) => {
  const hasAddress = address && (address.streetAddress || address.formattedAddress);

  // Build Google Maps URL
  const getGoogleMapsUrl = (addr: Address) => {
    const query = addr.formattedAddress ||
      [addr.streetAddress, addr.city, addr.state, addr.zipCode].filter(Boolean).join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Address</Subheading>
      <Divider className="my-4" />
      {!hasAddress ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No address recorded. Edit to add an address.
        </p>
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30">
            <MapPinIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            {address.streetAddress && (
              <Text className="font-medium text-zinc-900 dark:text-zinc-100">
                {address.streetAddress}
              </Text>
            )}
            {address.streetAddress2 && (
              <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                {address.streetAddress2}
              </Text>
            )}
            {(address.city || address.state || address.zipCode) && (
              <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                {[address.city, address.state].filter(Boolean).join(", ")}
                {address.zipCode && ` ${address.zipCode}`}
              </Text>
            )}
            <Link
              href={getGoogleMapsUrl(address)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              View on Google Maps
            </Link>
          </div>
        </div>
      )}
    </div>
  );
});

// Health & Additional Info Card
const HealthInfoCard: React.FC<ParentHealthInfoProps> = React.memo(({ parent }) => {
  const healthFields: HealthField[] = [
    { key: 'mentalHealthConcerns', label: 'Mental Health Concerns' },
    { key: 'isCurrentlyReceivingTherapy', label: 'Currently Receiving Therapy' },
    { key: 'historyOfSubstanceAbuse', label: 'History of Substance Abuse' }
  ];

  const hasHealthInfo = healthFields.some(({ key }) => parent[key]);
  const hasMedicalInfo = Boolean(parent.medicalProblems) || Boolean(parent.allergies);
  const hasNotes = Boolean(parent.notes);
  const hasAnyInfo = hasHealthInfo || hasMedicalInfo || hasNotes;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Health & Additional Info</Subheading>
      <Divider className="my-4" />
      {!hasAnyInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No health or additional information recorded. Edit to add health information or notes.
        </p>
      ) : (
        <DescriptionList>
          {healthFields.map(({ key, label }) =>
            parent[key] && (
              <React.Fragment key={key}>
                <DescriptionTerm>{label}</DescriptionTerm>
                <DescriptionDetails>
                  <Badge color="amber">Yes</Badge>
                </DescriptionDetails>
              </React.Fragment>
            )
          )}

          {parent.medicalProblems && (
            <>
              <DescriptionTerm>Medical Problems</DescriptionTerm>
              <DescriptionDetails className="whitespace-pre-wrap">
                {parent.medicalProblems}
              </DescriptionDetails>
            </>
          )}

          {parent.allergies && (
            <>
              <DescriptionTerm>Allergies</DescriptionTerm>
              <DescriptionDetails>{parent.allergies}</DescriptionDetails>
            </>
          )}

          {hasNotes && (
            <>
              <DescriptionTerm>Notes</DescriptionTerm>
              <DescriptionDetails className="whitespace-pre-wrap">
                {parent.notes}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

// Attorney & Legal Info Card
const AttorneyInfoCard: React.FC<{ parent: Parent }> = React.memo(({ parent }) => {
  const { attorneys } = useAttorney();

  // Get attorneys associated with this parent
  const parentAttorneys = (parent.attorneyIds || [])
    .map((id) => attorneys.find((a) => a.id === id))
    .filter(Boolean);

  const hasAttorneyInfo = parentAttorneys.length > 0 || parent.attorneyName || parent.attorneyPhone;
  const hasVictimizationInfo = parent.primaryVictimizationType || parent.offenderName;
  const hasAnyInfo = hasAttorneyInfo || hasVictimizationInfo;

  return (
    <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Attorney & Legal</Subheading>
      <Divider className="my-4" />
      {!hasAnyInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No attorney or legal information recorded.
        </p>
      ) : (
        <DescriptionList>
          {parentAttorneys.length > 0 && (
            <>
              <DescriptionTerm>Attorneys</DescriptionTerm>
              <DescriptionDetails>
                <div className="flex flex-col gap-1">
                  {parentAttorneys.map((attorney) => (
                    <Link
                      key={attorney!.id}
                      href={`/attorney/${attorney!.id}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {getAttorneyDisplayName(attorney!)}
                      {attorney!.firmName && (
                        <span className="text-zinc-500"> — {attorney!.firmName}</span>
                      )}
                    </Link>
                  ))}
                </div>
              </DescriptionDetails>
            </>
          )}

          {/* Legacy attorney fields - shown if no attorneyIds but has legacy data */}
          {parentAttorneys.length === 0 && parent.attorneyName && (
            <>
              <DescriptionTerm>Attorney Name</DescriptionTerm>
              <DescriptionDetails>{parent.attorneyName}</DescriptionDetails>
            </>
          )}

          {parentAttorneys.length === 0 && parent.attorneyPhone && (
            <>
              <DescriptionTerm>Attorney Phone</DescriptionTerm>
              <DescriptionDetails>{parent.attorneyPhone}</DescriptionDetails>
            </>
          )}

          {parent.primaryVictimizationType && (
            <>
              <DescriptionTerm>Victimization Type</DescriptionTerm>
              <DescriptionDetails>
                {parent.primaryVictimizationType === "other" && parent.primaryVictimizationOther
                  ? parent.primaryVictimizationOther
                  : VICTIMIZATION_TYPE_LABELS[parent.primaryVictimizationType as VictimizationType] || parent.primaryVictimizationType}
              </DescriptionDetails>
            </>
          )}

          {parent.offenderName && (
            <>
              <DescriptionTerm>Offender Name</DescriptionTerm>
              <DescriptionDetails>{parent.offenderName}</DescriptionDetails>
            </>
          )}

          {parent.secondaryVictimizationType && (
            <>
              <DescriptionTerm>Secondary Victimization</DescriptionTerm>
              <DescriptionDetails>
                {parent.secondaryVictimizationType === "other" && parent.secondaryVictimizationOther
                  ? parent.secondaryVictimizationOther
                  : VICTIMIZATION_TYPE_LABELS[parent.secondaryVictimizationType as VictimizationType] || parent.secondaryVictimizationType}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

const ParentDetailPage: React.FC = () => {
  const {
    selectedParent,
    loading,
    error,
    caseInfo,
    isChatEnabled,
  } = useParentDetail();

  // Early returns for loading and error states
  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedParent) {
    return <ErrorView error={new Error("Parent not found")} />;
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Parent Header with Status Badges */}
      <ParentHeader parent={selectedParent} isChatEnabled={isChatEnabled} />

      {/* Contact & Demographics - Two Column Layout on Desktop */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ContactInfoCard
          parent={selectedParent}
          caseInfo={caseInfo}
        />
        <DemographicsCard parent={selectedParent} />
      </div>

      {/* Address & Health Info */}
      <div className="grid gap-6 lg:grid-cols-2">
        <AddressCard address={selectedParent.address} />
        <HealthInfoCard parent={selectedParent} />
      </div>

      {/* Attorney Info */}
      <AttorneyInfoCard parent={selectedParent} />

      {/* Case Notes */}
      <ParentNotesCard parentId={selectedParent.id} />

      {/* Related Data Sections */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <ParentDetailFileList parentId={selectedParent.id} />
        </div>

        <div className="p-6 bg-white border shadow-sm rounded-xl dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <ParentDetailContactList parentId={selectedParent.id} />
        </div>
      </div>
    </div>
  );
};

export default ParentDetailPage;
